import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import RootLayout from "./layouts/RootLayout";
import PublicLayout from "./layouts/PublicLayout";
import PublicWithHeaderLayout from "./layouts/PublicWithHeaderLayout";
import DashboardLayout from "./layouts/DashboardLayout";
import InternalLayout from "./layouts/InternalLayout";
import OnboardingLayout from "./layouts/OnboardingLayout";
import ConfigurationLayout from "./layouts/ConfigurationLayout";

// Lazy load page components
const HomePage = lazy(() => import("./pages/HomePage"));

// Public pages
const AccountPage = lazy(() => import("./pages/public/AccountPage"));
const NotFoundPage = lazy(() => import("./pages/public/NotFoundPage"));
const SupportPage = lazy(() => import("./pages/public/SupportPage"));

// Onboarding pages
const BusinessTypeStep = lazy(
  () => import("./components/onboarding/BusinessTypeStep"),
);
const VideoSystemStep = lazy(
  () => import("./components/onboarding/VideoSystemStep"),
);
const CountryStep = lazy(() => import("./components/onboarding/CountryStep"));
const StateStep = lazy(() => import("./components/onboarding/StateStep"));
const InterestReasonStep = lazy(
  () => import("./components/onboarding/InterestReasonStep"),
);
const LocationCountStep = lazy(
  () => import("./components/onboarding/LocationCountStep"),
);
const CameraCountStep = lazy(
  () => import("./components/onboarding/CameraCountStep"),
);
const PricingStep = lazy(() => import("./components/onboarding/PricingStep"));
const WaitlistStep = lazy(() => import("./components/onboarding/WaitlistStep"));
const WaitlistCompletedStep = lazy(
  () => import("./components/onboarding/WaitlistCompletedStep"),
);
const EnterpriseStep = lazy(
  () => import("./components/onboarding/EnterpriseStep"),
);
const EnterpriseCompletedStep = lazy(
  () => import("./components/onboarding/EnterpriseCompletedStep"),
);
const AwaitingReturnStep = lazy(
  () => import("./components/onboarding/AwaitingReturnStep"),
);
const PaymentProcessingStep = lazy(
  () => import("./components/onboarding/PaymentProcessingStep"),
);
const OnboardingErrorStep = lazy(
  () => import("./components/onboarding/OnboardingErrorStep"),
);

// Dashboard pages
const AuthPage = lazy(() => import("./pages/dashboard/AuthPage"));
const SearchPage = lazy(() => import("./pages/dashboard/SearchPage"));
const SearchDetailsPage = lazy(
  () => import("./pages/dashboard/SearchDetailsPage"),
);
const StreamVideoPage = lazy(() => import("./pages/dashboard/StreamVideoPage"));
const StackedResultsPage = lazy(
  () => import("./pages/dashboard/StackedResultsPage"),
);
const UserPage = lazy(() => import("./pages/dashboard/UserPage"));
const ManageScheduledSearchesPage = lazy(
  () => import("./pages/dashboard/ManageScheduledSearchesPage"),
);
const IntegrationsPage = lazy(
  () => import("./pages/dashboard/IntegrationsPage"),
);

// Module pages
const ModulesPage = lazy(() => import("./pages/modules/ModulesPage"));
const MachineUsageSetupPage = lazy(
  () => import("./pages/modules/machine-usage/MachineUsageSetupPage"),
);
const MachineUsageDashboardPage = lazy(
  () => import("./pages/modules/machine-usage/MachineUsageDashboardPage"),
);
const MachineUsageConfigPage = lazy(
  () => import("./pages/modules/machine-usage/MachineUsageConfigPage"),
);
const HostGreetingTimePage = lazy(
  () => import("./pages/modules/restaurant-metrics/HostGreetingTimePage"),
);
const DrinkOrderPlacedPage = lazy(
  () => import("./pages/modules/restaurant-metrics/DrinkOrderPlacedPage"),
);
const VipPersonDetectionsPage = lazy(
  () => import("./pages/modules/VipPersonDetectionsPage"),
);
const WatchlistSetupPage = lazy(
  () => import("./pages/modules/watchlist/WatchlistSetupPage"),
);
const WatchlistPage = lazy(
  () => import("./pages/modules/watchlist/WatchlistPage"),
);

// Integration Subpages
const IntegrationSetupPage = lazy(
  () => import("./pages/integrations/IntegrationSetupPage"),
);
const IntegrationHelpPage = lazy(
  () => import("./pages/integrations/IntegrationHelpPage"),
);
const UserIntegrationPage = lazy(
  () => import("./pages/integrations/UserIntegrationPage"),
);
const TailgatingAlertPage = lazy(
  () => import("./pages/integrations/TailgatingAlertPage"),
);

// Internal/Admin pages
const AdminPage = lazy(() => import("./pages/internal/AdminPage"));
const BusinessesPage = lazy(() => import("./pages/internal/BusinessesPage"));
const BusinessCreatePage = lazy(
  () => import("./pages/internal/BusinessCreatePage"),
);
const BusinessDetailsPage = lazy(
  () => import("./pages/internal/BusinessDetailsPage"),
);
const CamerasPage = lazy(() => import("./pages/internal/CamerasPage"));
const CameraDetailsPage = lazy(
  () => import("./pages/internal/CameraDetailsPage"),
);
const LocationsPage = lazy(() => import("./pages/internal/LocationsPage"));
const LocationDetailsPage = lazy(
  () => import("./pages/internal/LocationDetailsPage"),
);
const ConnectionDetailsPage = lazy(
  () => import("./pages/internal/ConnectionDetailsPage"),
);

// Configuration pages
const ConfigurationPage = lazy(
  () => import("./pages/configuration/ConfigurationPage"),
);
const ConfigurationLocationDetailsPage = lazy(
  () => import("./pages/configuration/ConfigurationLocationDetailsPage"),
);
const ConfigurationConnectionPage = lazy(
  () => import("./pages/configuration/ConfigurationConnectionPage"),
);
const ConfigurationConnectionDetailsPage = lazy(
  () => import("./pages/configuration/ConfigurationConnectionDetailsPage"),
);
const ConfigurationCameraDetailsPage = lazy(
  () => import("./pages/configuration/ConfigurationCameraDetailsPage"),
);
const FaceReviewQueuePage = lazy(
  () => import("./pages/configuration/FaceReviewQueuePage"),
);

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Wrapper component to add Suspense around lazy-loaded components
const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: (
          <LazyWrapper>
            <HomePage />
          </LazyWrapper>
        ),
      },
      // Onboarding routes
      {
        path: "onboarding",
        element: (
          <LazyWrapper>
            <OnboardingLayout />
          </LazyWrapper>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="business-type" replace />,
          },
          {
            path: "business-type",
            element: <BusinessTypeStep />,
          },
          {
            path: "video-system",
            element: <VideoSystemStep />,
          },
          {
            path: "country",
            element: <CountryStep />,
          },
          {
            path: "state",
            element: <StateStep />,
          },
          {
            path: "interest-reason",
            element: <InterestReasonStep />,
          },
          {
            path: "location-count",
            element: <LocationCountStep />,
          },
          {
            path: "camera-count",
            element: <CameraCountStep />,
          },
          {
            path: "awaiting-return",
            element: <AwaitingReturnStep />,
          },
          {
            path: "pricing",
            element: <PricingStep />,
          },
          {
            path: "subscription-processing",
            element: <PaymentProcessingStep />,
          },
          {
            path: "waitlist",
            element: <WaitlistStep />,
          },
          {
            path: "waitlist-completed",
            element: <WaitlistCompletedStep />,
          },
          {
            path: "enterprise",
            element: <EnterpriseStep />,
          },
          {
            path: "enterprise-completed",
            element: <EnterpriseCompletedStep />,
          },
          {
            path: "error",
            element: <OnboardingErrorStep />,
          },
        ],
      },
      // Auth routes (public - for login/signup)
      {
        path: "account",
        element: (
          <LazyWrapper>
            <PublicLayout />
          </LazyWrapper>
        ),
        children: [
          {
            index: true,
            element: <AccountPage />,
          },
          {
            path: "*",
            element: <AccountPage />,
          },
        ],
      },
      {
        path: "support",
        element: (
          <LazyWrapper>
            <PublicWithHeaderLayout />
          </LazyWrapper>
        ),
        children: [
          {
            index: true,
            element: <SupportPage />,
          },
        ],
      },
      {
        path: "not-found",
        element: (
          <LazyWrapper>
            <PublicLayout />
          </LazyWrapper>
        ),
        children: [
          {
            index: true,
            element: <NotFoundPage />,
          },
        ],
      },
      // Dashboard routes (auth required)
      {
        path: "auth",
        element: (
          <LazyWrapper>
            <DashboardLayout />
          </LazyWrapper>
        ),
        children: [
          {
            index: true,
            element: <AuthPage />,
          },
        ],
      },
      {
        path: "search",
        element: (
          <LazyWrapper>
            <DashboardLayout />
          </LazyWrapper>
        ),
        children: [
          {
            index: true,
            element: <SearchPage />,
          },
          {
            path: "recurring",
            element: <ManageScheduledSearchesPage />,
          },
          {
            path: ":searchuuid",
            element: <SearchDetailsPage />,
          },
          {
            path: ":searchuuid/video",
            element: <StreamVideoPage />,
          },
          {
            path: ":searchuuid/stacked/:resultId",
            element: <StackedResultsPage />,
          },
        ],
      },
      {
        path: "user",
        element: (
          <LazyWrapper>
            <DashboardLayout />
          </LazyWrapper>
        ),
        children: [
          {
            index: true,
            element: <UserPage />,
          },
        ],
      },
      {
        path: "integrations",
        element: (
          <LazyWrapper>
            <DashboardLayout />
          </LazyWrapper>
        ),
        children: [
          {
            index: true,
            element: <IntegrationsPage />,
          },
          {
            path: "setup/:integrationId",
            element: <IntegrationSetupPage />,
          },
          {
            path: "help/:integrationId",
            element: <IntegrationHelpPage />,
          },
        ],
      },
      {
        path: "location/:locationId/integrations/:integrationId",
        element: (
          <LazyWrapper>
            <DashboardLayout />
          </LazyWrapper>
        ),
        children: [
          {
            index: true,
            element: <UserIntegrationPage />,
          },
          {
            path: "tailgating",
            element: <TailgatingAlertPage />,
          },
        ],
      },
      // Module routes
      {
        path: "modules",
        element: (
          <LazyWrapper>
            <DashboardLayout />
          </LazyWrapper>
        ),
        children: [
          {
            index: true,
            element: <ModulesPage />,
          },
          {
            path: "setup/machine-usage",
            element: <MachineUsageSetupPage />,
          },
          {
            path: "machine-usage",
            element: <MachineUsageDashboardPage />,
          },
          {
            path: "machine-usage/config",
            element: <MachineUsageConfigPage />,
          },
          {
            path: "restaurant-metrics",
            element: <HostGreetingTimePage />,
          },
          {
            path: "first-drink-order",
            element: <DrinkOrderPlacedPage />,
          },
          {
            path: "persons/:personId/detections",
            element: <VipPersonDetectionsPage />,
          },
          {
            path: "setup/watchlist",
            element: <WatchlistSetupPage />,
          },
          {
            path: "watchlist",
            element: <WatchlistPage />,
          },
        ],
      },
      // Configuration routes
      {
        path: "configure",
        element: (
          <LazyWrapper>
            <ConfigurationLayout />
          </LazyWrapper>
        ),
        children: [
          {
            index: true,
            element: <ConfigurationPage />,
          },
          {
            path: ":businessId",
            element: <ConfigurationPage />,
          },
          {
            path: ":businessId/locations/:locationId",
            element: <ConfigurationLocationDetailsPage />,
          },
          {
            path: ":businessId/connections",
            element: <ConfigurationConnectionPage />,
          },
          {
            path: ":businessId/connections/:connectionId",
            element: <ConfigurationConnectionDetailsPage />,
          },
          {
            path: ":businessId/connections/:connectionId/cameras/:cameraId",
            element: <ConfigurationCameraDetailsPage />,
          },
          {
            path: ":businessId/locations/:locationId/face-reviews",
            element: <FaceReviewQueuePage />,
          },
        ],
      },
      // Admin routes (INTERNAL user type required)
      {
        path: "admin",
        element: (
          <LazyWrapper>
            <InternalLayout />
          </LazyWrapper>
        ),
        children: [
          {
            index: true,
            element: <AdminPage />,
          },
          {
            path: "businesses",
            element: <BusinessesPage />,
          },
          {
            path: "businesses/create",
            element: <BusinessCreatePage />,
          },
          {
            path: "businesses/:businessId",
            element: <BusinessDetailsPage />,
          },
          {
            path: "cameras",
            element: <CamerasPage />,
          },
          {
            path: "cameras/:cameraId",
            element: <CameraDetailsPage />,
          },
          {
            path: "locations",
            element: <LocationsPage />,
          },
          {
            path: "locations/:locationId",
            element: <LocationDetailsPage />,
          },
          {
            path: "connections/:connectionId",
            element: <ConnectionDetailsPage />,
          },
        ],
      },
      // Catch-all route for 404s
      {
        path: "*",
        element: (
          <LazyWrapper>
            <NotFoundPage />
          </LazyWrapper>
        ),
      },
    ],
  },
]);
